/** The UI-language switch: a globe with the current language's short
 *  label, opening the searchable picker.
 *
 *  From ``sm`` up the picker opens in a popover under the trigger; on
 *  a phone (``xs``) it takes the whole screen, with a title bar, the
 *  search field pinned under it and the list filling the rest.
 *  ``asMenuItem`` renders a row for an overflow menu instead, which
 *  opens the same dialog (so there is no menu inside a menu). Works
 *  signed out too (login, landing): the pick is stored on the device
 *  and saved to the account after sign-in. */
import { Close as CloseIcon, Language as LanguageIcon } from "@/components/icons";
import * as React from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useUiLanguage } from "@/i18n/useUiLanguage";

import { LanguagePicker } from "./LanguagePicker";

export interface LanguageMenuButtonProps {
  /** Render an overflow-menu row ("Language: Svenska") instead. */
  asMenuItem?: boolean;
  /** Called once the picker closes (after a pick or a dismiss), so an
   *  enclosing menu can close too. */
  onDone?: () => void;
}

export function LanguageMenuButton({
  asMenuItem = false,
  onDone,
}: LanguageMenuButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"));
  const { code, entry, setUiLanguage } = useUiLanguage();
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const titleId = React.useId();

  const endonym = entry?.endonym ?? code;
  const triggerLabel = t("language_picker.trigger_label", { language: endonym });

  const close = () => {
    setAnchor(null);
    setDialogOpen(false);
    onDone?.();
  };
  const pick = (next: string | null) => {
    if (next && next !== code) setUiLanguage(next);
    close();
  };
  const open = (target: HTMLElement) => {
    // The overflow row always uses the dialog: a popover anchored to a
    // menu row would sit on top of the menu it came from.
    if (phone || asMenuItem) setDialogOpen(true);
    else setAnchor(target);
  };

  // The popover and dialog are portaled, but React still bubbles their
  // keydowns up the component tree. As an overflow-menu row that tree
  // runs through the Menu's MenuList, whose type-ahead would swallow
  // letters, whose arrows would move focus behind the modal and whose
  // Tab would close the menu (and the dialog with it). Keep the keys
  // here; the Modal still closes on Escape, since its handler checks
  // the key, not whether the event propagates.
  const keepKeys = (e: React.KeyboardEvent) => e.stopPropagation();

  const picker = (touch: boolean, listMaxHeight?: string) => (
    <LanguagePicker
      kind="ui"
      variant="list"
      value={code}
      onChange={pick}
      onClose={close}
      touch={touch}
      listMaxHeight={listMaxHeight}
    />
  );

  const trigger = asMenuItem ? (
    <MenuItem onClick={(e) => open(e.currentTarget)} aria-haspopup="dialog">
      <ListItemIcon>
        <LanguageIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primary={`${t("common.language")}: ${endonym}`} />
    </MenuItem>
  ) : (
    <Tooltip title={triggerLabel}>
      <Button
        variant="text"
        color="inherit"
        onClick={(e) => open(e.currentTarget)}
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        startIcon={<LanguageIcon />}
        sx={{ minWidth: 0, px: 1, "& .MuiButton-startIcon": { mr: 0.5 } }}
      >
        {/* nowrap: a CJK short label ("日本語") otherwise breaks
            between glyphs when the app bar is crowded. */}
        <Typography
          variant="button"
          component="span"
          lang={entry?.code}
          dir={entry?.dir}
          sx={{ whiteSpace: "nowrap" }}
        >
          {entry?.short ?? code.toUpperCase()}
        </Typography>
      </Button>
    </Tooltip>
  );

  return (
    <>
      {trigger}
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={close}
        onKeyDown={keepKeys}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: "calc(100vw - 32px)", p: 1 } } }}
      >
        {picker(false, "60vh")}
      </Popover>
      <Dialog
        open={dialogOpen}
        onClose={close}
        onKeyDown={keepKeys}
        fullScreen={phone}
        fullWidth
        maxWidth="xs"
        aria-labelledby={titleId}
      >
        <AppBar
          position="static"
          color="default"
          elevation={0}
          sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" }}
        >
          <Toolbar variant="dense" sx={{ gap: 1 }}>
            <Typography id={titleId} variant="h6" component="h2" sx={{ flex: 1 }}>
              {t("language_picker.dialog_title")}
            </Typography>
            <IconButton edge="end" onClick={close} aria-label={t("common.close")}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            // The search field stays pinned; the list (the popper, a
            // sibling of the field) fills the rest and scrolls itself.
            "& .MuiAutocomplete-root": { flex: "none" },
            "& .MuiAutocomplete-popper": { flex: 1, minHeight: 0, display: "flex", mt: 1 },
            "& .MuiAutocomplete-paper": { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
            "& .MuiAutocomplete-listbox": { flex: 1, maxHeight: phone ? "none" : "60vh" },
          }}
        >
          {picker(phone)}
        </Box>
      </Dialog>
    </>
  );
}
